// Quick post: turns a listing written for LINE or a Facebook group into form fields, so a lister can paste
// what they already have instead of typing it again. Everything it fills is shown for checking before saving.
// Thai and English keywords the parser looks for, not text shown to users.
/* th-only:start */
const num = (s) => Number(String(s).replace(/,/g, ''));

const TYPES = [ // first match wins, so specific words come before general ones
  ['lease_transfer', /เซ้ง|lease\s*transfer/i], ['down_payment', /ขายดาวน์|down\s*payment/i],
  ['rent', /ให้เช่า|ปล่อยเช่า|เช่า|for\s*rent|\brent\b/i], ['sale', /ขาย|for\s*sale|\bsale\b/i],
];
const CATEGORIES = [
  ['condo', /คอนโด|condo/i], ['townhouse', /ทาวน์โฮม|ทาวน์เฮ้าส์|ทาวน์เฮาส์|town\s*(home|house)/i], ['apartment', /อพาร์ทเมนท์|อพาร์ตเมนต์|apartment/i],
  ['hotel_business', /โรงแรม|hotel|รีสอร์ท|resort/i], ['factory', /โรงงาน|factory/i], ['warehouse', /โกดัง|คลังสินค้า|warehouse/i],
  ['showroom', /โชว์รูม|showroom/i], ['office', /สำนักงาน|ออฟฟิศ|office/i], ['commercial', /อาคารพาณิชย์|ตึกแถว|shop\s*house/i],
  ['building', /อาคาร|ตึก|building/i], ['land', /ที่ดิน|\bland\b/i], ['house', /บ้านเดี่ยว|บ้าน|house|villa/i], ['other_business', /กิจการ|business/i],
];
const AMENITIES = [
  ['parking', /ที่จอดรถ|จอดรถ|parking/i], ['pool', /สระว่ายน้ำ|pool/i], ['gym', /ฟิตเนส|fitness|gym/i], ['elevator', /ลิฟต์|ลิฟท์|lift|elevator/i],
  ['security', /รปภ|รักษาความปลอดภัย|security|cctv/i], ['garden', /สวน|garden/i], ['balcony', /ระเบียง|balcony/i],
  ['air_conditioning', /แอร์|เครื่องปรับอากาศ|air\s*con/i], ['furnished', /เฟอร์นิเจอร์|furnished|fully\s*fitted/i], ['pet_friendly', /เลี้ยงสัตว์|pet/i],
];

export function parseListingText(raw, provinces = []) {
  const text = String(raw || '').replace(/\r/g, '').trim();
  const out = {};
  if (!text) return out;
  for (const [v, re] of TYPES) if (re.test(text)) { out.listing_type = v; break; }
  for (const [v, re] of CATEGORIES) if (re.test(text)) { out.property_type = v; break; }

  const million = text.match(/(\d[\d,]*(?:\.\d+)?)\s*(ล้าน|ลบ\.?|million|\bm\b)/i);
  const labelled = text.match(/(?:ราคา|ค่าเช่า|ค่าเซ้ง|price|rent)\D{0,15}?(\d{1,3}(?:,\d{3})+|\d{3,})/i);
  const money = text.match(/(?:฿\s*(\d[\d,]*))|(?:(\d{1,3}(?:,\d{3})+|\d{4,})\s*(?:บาท|thb|\/\s*เดือน|ต่อเดือน|฿))/i);
  if (million) out.price = Math.round(num(million[1]) * 1e6);
  else if (labelled) out.price = num(labelled[1]);
  else if (money) out.price = num(money[1] || money[2]);

  const bed = text.match(/(\d{1,2})\s*(?:ห้องนอน|นอน|bed(?:room)?s?\b|br\b)/i);
  const bath = text.match(/(\d{1,2})\s*(?:ห้องน้ำ|น้ำ|bath(?:room)?s?\b)/i);
  if (bed) out.bedrooms = Number(bed[1]);
  const floor = text.match(/ชั้น(?:ที่)?\s*(\d{1,3})(?!\s*ชั้น)|(\d{1,3})(?:st|nd|rd|th)\s*floor|\bfloor\s*(\d{1,3})\b/i);
  if (floor) out.floor_level = Number(floor[1] || floor[2] || floor[3]);
  if (bath) out.bathrooms = Number(bath[1]);

  const sqm = text.match(/(\d[\d,]*(?:\.\d+)?)\s*(?:ตร\.?\s?ม\.?|ตารางเมตร|sq\.?\s?m\b|sqm|m²|m2\b)/i);
  const wah = text.match(/(\d[\d,]*(?:\.\d+)?)\s*(?:ตร\.?\s?ว\.?|ตารางวา|sq\.?\s?wah)/i);
  const rai = text.match(/(\d[\d,]*(?:\.\d+)?)\s*ไร่/);
  if (rai) { out.area_value = num(rai[1]); out.area_unit = 'rai'; }
  else if (wah && (out.property_type === 'land' || !sqm)) { out.area_value = num(wah[1]); out.area_unit = 'sq_wah'; }
  else if (sqm) { out.area_value = num(sqm[1]); out.area_unit = 'sqm'; }

  const transit = text.match(/((?:BTS|MRT|ARL|Airport\s*Rail\s*Link|แอร์พอร์ต\s*ลิงก์|รถไฟฟ้า)[^\n,|]{2,40})/i);
  if (transit) out.nearby_transit = transit[1].split(/\s(?=เขต|แขวง|อำเภอ|ตำบล|จังหวัด|กรุงเทพ|ราคา|โทร)/)[0].trim().slice(0, 80);
  const district = text.match(/(?:เขต|อำเภอ|อ\.)\s*([ก-๙]{2,30})/);
  if (district) out.city = district[1];
  for (const [th, , , en] of provinces) if (text.includes(th) || (en && new RegExp(`\\b${en}\\b`, 'i').test(text))) { out.state = th; break; }
  const project = text.match(/(?:โครงการ|project)\s*[:：]?\s*([^\n,|]{2,60})/i);
  if (project) out.project_name = project[1].trim();

  const phone = text.match(/0[689]\d[\s-]?\d{3}[\s-]?\d{4}/);
  if (phone) out.contact_phone = phone[0].replace(/\D/g, '');
  const line = text.match(/(?:line|ไลน์)\s*(?:id)?\s*[:：]?\s*(@?[A-Za-z0-9._-]{2,40})/i);
  if (line) out.contact_line_id = line[1];

  const amenities = AMENITIES.filter(([, re]) => re.test(text)).map(([v]) => v);
  if (amenities.length) out.amenities = amenities;
  const first = text.split('\n').map((l) => l.trim()).find((l) => l.length >= 10);
  if (first) out.title = first.replace(/[*#•]+/g, '').trim().slice(0, 120);
  if (text.length >= 30) out.description = text.slice(0, 5000);
  return out;
}
/* th-only:end */
